import { managesPage } from "./profilePageSetup";

export const ACCOUNT_ROLES = Object.freeze(["GUEST", "ARTIST", "VENUE", "STUDIO", "MEDIA"]);
export const AUTHENTICATED_ROLES = Object.freeze([...ACCOUNT_ROLES, "ADMIN"]);

export const PERMISSIONS = Object.freeze({
  INBOX: "inbox",
  CONTACT_ARTIST: "contactArtist",
  CONTACT_VENUE: "contactVenue",
  CONTACT_STUDIO: "contactStudio",
  INVITE_ARTIST_TO_GIG: "inviteArtistToGig",
  APPLY_TO_GIG: "applyToGig",
  POST_GIG: "postGig",
  REQUEST_STUDIO_SESSION: "requestStudioSession",
  RATE_ARTIST_AFTER_GIG: "rateArtistAfterGig",
  RATE_ARTIST_AFTER_SESSION: "rateArtistAfterSession",
  RATE_VENUE_AFTER_GIG: "rateVenueAfterGig",
  RATE_STUDIO_AFTER_SESSION: "rateStudioAfterSession",
  CREATE_ARTIST_PAGE: "createArtistPage",
  CREATE_VENUE_PAGE: "createVenuePage",
  CREATE_STUDIO_PAGE: "createStudioPage",
  CREATE_MEDIA_PAGE: "createMediaPage",
  CREATE_STUDIO_BUNDLE: "createStudioBundle",
  ADMIN: "admin",
});

const ROLE_PERMISSIONS = Object.freeze({
  GUEST: new Set([
    PERMISSIONS.INBOX,
    PERMISSIONS.CONTACT_ARTIST,
    PERMISSIONS.CONTACT_VENUE,
    PERMISSIONS.CONTACT_STUDIO,
  ]),
  ARTIST: new Set([
    PERMISSIONS.INBOX,
    PERMISSIONS.CONTACT_ARTIST,
    PERMISSIONS.CONTACT_VENUE,
    PERMISSIONS.CONTACT_STUDIO,
    PERMISSIONS.APPLY_TO_GIG,
    PERMISSIONS.REQUEST_STUDIO_SESSION,
    PERMISSIONS.RATE_VENUE_AFTER_GIG,
    PERMISSIONS.RATE_STUDIO_AFTER_SESSION,
    PERMISSIONS.CREATE_ARTIST_PAGE,
  ]),
  VENUE: new Set([
    PERMISSIONS.INBOX,
    PERMISSIONS.CONTACT_ARTIST,
    PERMISSIONS.CONTACT_VENUE,
    PERMISSIONS.CONTACT_STUDIO,
    PERMISSIONS.INVITE_ARTIST_TO_GIG,
    PERMISSIONS.POST_GIG,
    PERMISSIONS.RATE_ARTIST_AFTER_GIG,
    PERMISSIONS.CREATE_VENUE_PAGE,
  ]),
  STUDIO: new Set([
    PERMISSIONS.INBOX,
    PERMISSIONS.CONTACT_ARTIST,
    PERMISSIONS.CONTACT_VENUE,
    PERMISSIONS.CONTACT_STUDIO,
    PERMISSIONS.RATE_ARTIST_AFTER_SESSION,
    PERMISSIONS.CREATE_STUDIO_PAGE,
  ]),
  MEDIA: new Set([
    PERMISSIONS.INBOX,
    PERMISSIONS.CONTACT_ARTIST,
    PERMISSIONS.CONTACT_VENUE,
    PERMISSIONS.CONTACT_STUDIO,
    PERMISSIONS.CREATE_MEDIA_PAGE,
  ]),
  ADMIN: new Set([PERMISSIONS.ADMIN]),
});

export function isAuthenticatedUser(user) {
  return Boolean(user && AUTHENTICATED_ROLES.includes(user.role));
}

export function hasPermission(user, permission) {
  return Boolean(user && ROLE_PERMISSIONS[user.role]?.has(permission));
}

export function canInviteArtistsToVenue(user, profile, venueId) {
  return hasPermission(user, PERMISSIONS.INVITE_ARTIST_TO_GIG)
    && managesPage(profile, "venuePages", venueId);
}

export function canEditEvent(user, event) {
  return hasPermission(user, PERMISSIONS.POST_GIG)
    && Boolean(user?.id && event?.ownerUserId && user.id === event.ownerUserId);
}

export function canRateEventLineup(user, event, now = new Date()) {
  if (!canEditEvent(user, event)) return false;
  const eventEnd = new Date(event?.endAt || event?.startAt || event?.date);
  return Number.isFinite(eventEnd.getTime()) && eventEnd <= now;
}

export function getAuthGateState(user, { loading = false, permission } = {}) {
  if (loading) return "loading";
  if (!user) return "sign-in";
  if (permission && !hasPermission(user, permission)) return "forbidden";
  return "allowed";
}
